import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';
import type { CompletionReportListItem } from './completionReports';
import type { PostMortemListItem } from './postMortems';

export interface Plan {
  id: string;
  title: string;
  /** V2 initial detail omits this derived snapshot; V1 detail/list paths still include it. */
  content?: string;
  contentTokenCount?: number | null;
  type?: string | null;
  estimatedComplexity?: string | null;
  complexity?: string | null;
  /** 플랜 생성 출처. NORMAL | QUICK | CODE_REVIEW. 퀵·코드리뷰 플랜은 HTML 프리뷰 탭을 숨긴다. */
  kind?: string | null;
  status: string;
  /** 읽기 노출 범위. PRIVATE(작성자·담당자·관리자만) | PROJECT(프로젝트 멤버 전원) */
  visibility?: string | null;
  /** 실행 주체(러너/에이전트) 스냅샷 */
  assignedTo: string | null;
  assignedToName?: string | null;
  /** 책임 주체(사람 담당자) Member id */
  assigneeMemberId?: string | null;
  assigneeName?: string | null;
  priority: string;
  runnerType?: string | null;
  model?: string | null;
  startRunnerType?: string | null;
  startModel?: string | null;
  startEffort?: string | null;
  fastMode?: boolean | null;
  createdByMemberId?: string | null;
  createdByAgentId?: string | null;
  createdByType?: string | null;
  /** 경유 축. 도구 축을 특정하지 못해도 웹 작성물과 에이전트 표면 작성물을 구분한다. */
  createdByCredentialKind?: string | null;
  createdByName?: string | null;
  createdByAgentName?: string | null;
  /** 플랜 데이터 버전. V1=content 단일 본문, V2=구조화 sections/tasks가 SSOT. */
  contentVersion?: string | null;
  /** v2 구조화 진행률(목록/상세에서 노출 가능). V1은 없음. */
  progress?: PlanProgress | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** v2 구조화 플랜의 진행률 집계(api derivePlanProgress와 대응). */
export interface PlanProgress {
  total: number;
  todo: number;
  inProgress: number;
  done: number;
  skipped: number;
  blocked: number;
  completed: number;
  percent: number;
}

/** v2 구조화 섹션. V1 플랜에는 없다. */
export interface PlanSection {
  id: string;
  kind: string;
  title?: string | null;
  /** 초기 상세에서는 TLDR만 포함한다. 나머지는 section body endpoint로 지연 조회한다. */
  content?: string;
  orderIndex: number;
}

/** v2 구조화 작업(TODO). status에서 plan progress/PARTIAL이 파생된다. */
export interface PlanTask {
  id: string;
  /**
   * 표시 번호(1-base). 서버가 위상 정렬 서수로 매겨 내려준다. 배포 시차·캐시 응답에는 없을 수 있다.
   * 상세 응답이 태스크 상한으로 절단되면 잘린 태스크의 번호가 비어 1부터 연속하지 않는다 —
   * 번호를 배열 위치로 되짚지 말 것(단건 조회 번호와 맞추기 위해 전체 집합 기준으로 매긴다).
   */
  number?: number;
  title: string;
  detail?: string | null;
  status: string;
  orderIndex: number;
  wave?: number | null;
  category?: string | null;
  completionReportId?: string | null;
  /** 초기 상세는 detail을 항상 생략하므로, 펼침 가능 여부는 이 플래그로 판단한다. */
  hasDetail?: boolean;
  /** 접힌 상태에서도 개수를 보여주기 위한 루트 코멘트 수. 목록의 meta.total과 같은 정의다. */
  commentCount?: number;
  dependsOnTaskIds: string[];
}

export interface PlanSectionBody {
  id: string;
  content: string;
}

export interface PlanTaskBody {
  id: string;
  detail: string | null;
}

/** 편집 진입용 전체 구조화 계약(캡 없음). 초기 상세 summary와 달리 메타+본문 full row를 담는다. */
export interface PlanStructuredBodies {
  sections: PlanSection[];
  tasks: PlanTask[];
}

export type PlanSectionInput = {
  kind: string;
  title?: string | null;
  content: string;
  orderIndex?: number;
};

export type PlanTaskInput = {
  /** 기존 PlanTask.id. 본문 수정에서만 의미가 있고, 신규 태스크에는 넣지 않는다. */
  id?: string;
  title: string;
  detail?: string | null;
  status?: string;
  orderIndex?: number;
  wave?: number | null;
  category?: string | null;
  dependsOnTaskOrderIndexes?: number[];
};

type PlanListResponse = {
  data: Plan[];
};

type PlanPagedResponse = {
  data: Plan[];
  meta: PaginationMeta;
};

type PlanResponse = {
  data: Plan;
};

export const fetchPlans = async (
  token: string,
  projectId: string,
  params?: {
    search?: string;
    title?: string;
    status?: string;
    type?: string;
    priority?: string;
    assignedTo?: string;
    createdByMemberId?: string;
  },
): Promise<Plan[]> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.title) queryParams.set('title', params.title);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.type) queryParams.set('type', params.type);
  if (params?.priority) queryParams.set('priority', params.priority);
  if (params?.assignedTo) queryParams.set('assignedTo', params.assignedTo);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);

  const url = `${getApiUrl()}/api/projects/${projectId}/plans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plans: ${response.status}`);
  }

  const data = (await response.json()) as PlanListResponse;
  return data.data;
};

export const fetchPlansPaged = async (
  token: string,
  projectId: string,
  params?: {
    search?: string;
    title?: string;
    status?: string;
    type?: string;
    priority?: string;
    assignedTo?: string;
    createdByMemberId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  },
): Promise<PagedData<Plan>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.title) queryParams.set('title', params.title);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.type) queryParams.set('type', params.type);
  if (params?.priority) queryParams.set('priority', params.priority);
  if (params?.assignedTo) queryParams.set('assignedTo', params.assignedTo);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);
  if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
  if (params?.dateTo) queryParams.set('dateTo', params.dateTo);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/projects/${projectId}/plans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plans: ${response.status}`);
  }

  const data = (await response.json()) as PlanPagedResponse;
  return data;
};

export const fetchPlanById = async (token: string, projectId: string, id: string): Promise<Plan> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plan: ${response.status}`);
  }

  const data = (await response.json()) as PlanResponse;
  return data.data;
};

export interface PlanDetailData {
  plan: Plan;
  dependencies: PlanDependencies;
  originIssues: PlanOriginIssue[];
  documentLinks: PlanDocumentLink[];
  completionReports: CompletionReportListItem[];
  completionReportsTotal?: number;
  reviewReport?: CompletionReportListItem | null;
  postMortems: PostMortemListItem[];
  postMortemsTotal?: number;
  /** v2 구조화 데이터. V1 플랜은 sections/tasks 빈 배열 + progress null. */
  sections?: PlanSection[];
  tasks?: PlanTask[];
  progress?: PlanProgress | null;
}

type PlanDetailResponse = {
  data: PlanDetailData;
};

export const fetchPlanDetail = async (token: string, projectId: string, id: string): Promise<PlanDetailData> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}/detail`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plan detail: ${response.status}`);
  }

  const data = (await response.json()) as PlanDetailResponse;
  return data.data;
};

export const fetchPlanSectionBody = async (
  token: string,
  projectId: string,
  planId: string,
  sectionId: string,
): Promise<PlanSectionBody> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/sections/${sectionId}/content`,
    { cache: 'no-store', headers: authHeaders(token) },
  );
  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plan section body: ${response.status}`);
  }
  return ((await response.json()) as { data: PlanSectionBody }).data;
};

export const fetchPlanTaskBody = async (
  token: string,
  projectId: string,
  planId: string,
  taskId: string,
): Promise<PlanTaskBody> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/tasks/${taskId}/detail`,
    { cache: 'no-store', headers: authHeaders(token) },
  );
  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plan task body: ${response.status}`);
  }
  return ((await response.json()) as { data: PlanTaskBody }).data;
};

export const fetchPlanStructuredBodies = async (
  token: string,
  projectId: string,
  planId: string,
): Promise<PlanStructuredBodies> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/structured-bodies`,
    { cache: 'no-store', headers: authHeaders(token) },
  );
  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch structured plan bodies: ${response.status}`);
  }
  return ((await response.json()) as { data: PlanStructuredBodies }).data;
};

/**
 * 플랜 상세 read-only GraphQL client (Mercurius).
 *
 * REST `fetchPlanDetail`과 동일한 `PlanDetailData`로 정규화해 반환하므로 호출부/상세 UI 모델은 동일하다.
 * 전체 플랜 상세 경로의 기본 조회로 쓰이며(`usePlanDetail`), REST 폴백은 없다
 * (폴백이 있는 것은 홈 활동 패널의 `useActivityDetail`이다).
 * resolver가 REST detail route와 동일한 shape를 반환하므로 정규화는 payload 추출/타이핑에 그친다.
 *
 * 이 초기 상세 payload는 V1/V2 모두 `Plan.content`를 담지 않는다. 본문이 필요한 화면은
 * REST `GET /plans/:id`(`usePlan`)로 지연 로딩한다.
 */
const PLAN_DETAIL_QUERY = `
  query PlanDetail($projectId: ID!, $id: ID!) {
    planDetail(projectId: $projectId, id: $id) {
      plan
      dependencies
      originIssues
      documentLinks
      completionReports
      completionReportsTotal
      reviewReport
      postMortems
      postMortemsTotal
      sections
      tasks
      progress
    }
  }
`;

type PlanDetailGraphqlPayload = {
  data?: { planDetail: PlanDetailData | null } | null;
  errors?: Array<{ message: string }>;
};

export const fetchPlanDetailGraphql = async (token: string, projectId: string, id: string): Promise<PlanDetailData> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/graphql`, {
    method: 'POST',
    cache: 'no-store',
    headers: authHeaders(token, true),
    body: JSON.stringify({ query: PLAN_DETAIL_QUERY, variables: { projectId, id }, operationName: 'PlanDetail' }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plan detail: ${response.status}`);
  }

  const payload = (await response.json()) as PlanDetailGraphqlPayload;
  if (payload.errors && payload.errors.length > 0) {
    throw new Error(payload.errors[0]?.message ?? 'GraphQL plan detail failed');
  }
  const detail = payload.data?.planDetail;
  if (!detail) {
    throw new Error('GraphQL plan detail returned no data');
  }
  return detail;
};

export const createPlan = async (
  token: string,
  projectId: string,
  input: {
    title: string;
    content: string;
    type?: string;
    priority: string;
    complexity: string;
  },
): Promise<Plan> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create plan: ${response.status}`);
  }

  const data = (await response.json()) as PlanResponse;
  return data.data;
};

export type UpdatePlanInput = Partial<
  Pick<Plan, 'title' | 'content' | 'type' | 'status' | 'priority' | 'complexity' | 'visibility'>
> & {
  /** complexity 변경 사유. 서버가 MODIFICATION 코멘트로 기록한다. Plan 필드가 아니라 update 전용 입력. */
  complexityChangeReason?: string;
  /** v2 구조화 플랜 정식 편집 입력. content 재파싱 대신 sections/tasks를 직접 교체한다. */
  sections?: PlanSectionInput[];
  tasks?: PlanTaskInput[];
};

export type StartPlanInput = {
  assignedTo?: string;
  targetDaemonId?: string;
  /** null은 "엔진 없음" 확정이다. 키를 생략하면 서버가 저장된 시작 정책·생성 스냅샷으로 폴백한다. */
  runnerType?: string | null;
  model?: string | null;
  /** 명시 모델이 있을 때만 싣는다. null을 명시하면 저장된 시작 effort가 무시되므로, 없으면 키를 생략한다. */
  effort?: string | null;
  fastMode?: boolean;
};

export type StartPlanResult = {
  id: string;
  plan: Plan;
  completionReport: CompletionReportListItem | null;
  /** 이미 IN_PROGRESS였던 플랜에 start를 보냈다. 서버는 아무것도 바꾸지 않았다. */
  alreadyStarted?: boolean;
};

export const startPlan = async (
  token: string,
  projectId: string,
  id: string,
  input: StartPlanInput = {},
): Promise<StartPlanResult> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}/start`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to start plan: ${response.status}`);
  }

  const data = (await response.json()) as { data: StartPlanResult };
  return data.data;
};

export type PlanWorkflowRunSummary = {
  id: string;
  planId: string;
  status: string;
  // 자동 발행이 꺼진 런은 "곧 시작됨"이 아니라 "막힘"이다. 사유(WorkflowDispatchBlockReason)는
  // 관리자가 직접 끈 경우 null이므로, 막힘 판정은 두 필드를 함께 본다.
  dispatchEnabled: boolean;
  dispatchDisabledReason: string | null;
  createdAt: string;
};

export type PlanWorkflowRunNode = {
  planTaskId: string;
  title: string;
  status: string;
  isReady: boolean;
};

export type PlanWorkflowRunGraph = PlanWorkflowRunSummary & {
  nodes: PlanWorkflowRunNode[];
  readyPlanTaskIds: string[];
};

export const fetchPlanWorkflowRuns = async (
  token: string,
  projectId: string,
  planId: string,
): Promise<PagedData<PlanWorkflowRunSummary>> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/workflow-runs?page=1&pageSize=1`,
    { cache: 'no-store', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plan workflow runs: ${response.status}`);
  }

  return (await response.json()) as PagedData<PlanWorkflowRunSummary>;
};

export const fetchPlanWorkflowRunGraph = async (
  token: string,
  projectId: string,
  planId: string,
  workflowRunId: string,
): Promise<PlanWorkflowRunGraph> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/workflow-runs/${workflowRunId}`,
    { cache: 'no-store', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plan workflow run graph: ${response.status}`);
  }

  const data = (await response.json()) as { data: PlanWorkflowRunGraph };
  return data.data;
};

export const updatePlan = async (
  token: string,
  projectId: string,
  id: string,
  input: UpdatePlanInput,
): Promise<Plan> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update plan: ${response.status}`);
  }

  const data = (await response.json()) as PlanResponse;
  return data.data;
};

export interface PlanTaskStatusResult {
  planStatus: string;
  tasks: PlanTask[];
  progress: PlanProgress | null;
}

export type PlanTaskExecutionPolicyInput = {
  engine: string | null;
  model: string | null;
  effort?: string | null;
};

export type PlanTaskExecutionPolicyResult = PlanTaskExecutionPolicyInput & {
  taskId: string;
  unresolvedEngine: string | null;
};

/** 시작 정책 저장 응답. 서버가 정규화한 값이며 태스크 정책과 달리 taskId/unresolvedEngine이 없다. */
export type PlanStartPolicyResult = PlanTaskExecutionPolicyInput;

export type PlanCodeReviewPolicyInput = {
  autoReview: boolean;
  engine: string | null;
  model: string | null;
  effort?: string | null;
};

/** v2 구조화 작업의 상태를 변경한다. 서버가 plan status/progress를 재파생해 돌려준다. */
export const patchPlanTaskStatus = async (
  token: string,
  projectId: string,
  planId: string,
  taskId: string,
  status: string,
): Promise<PlanTaskStatusResult> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/tasks/${taskId}/status`,
    {
      method: 'PATCH',
      headers: authHeaders(token, true),
      body: JSON.stringify({ status }),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update task status: ${response.status}`);
  }

  const data = (await response.json()) as { data: PlanTaskStatusResult };
  return data.data;
};

export const patchPlanTaskExecutionPolicy = async (
  token: string,
  projectId: string,
  planId: string,
  taskId: string,
  input: PlanTaskExecutionPolicyInput,
): Promise<PlanTaskExecutionPolicyResult> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/tasks/${taskId}/execution-policy`,
    {
      method: 'PATCH',
      headers: authHeaders(token, true),
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update task execution policy: ${response.status}`);
  }

  const data = (await response.json()) as { data: PlanTaskExecutionPolicyResult };
  return data.data;
};

export const patchPlanCodeReviewPolicy = async (
  token: string,
  projectId: string,
  planId: string,
  input: PlanCodeReviewPolicyInput,
): Promise<PlanCodeReviewPolicyInput> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/code-review-policy`,
    {
      method: 'PATCH',
      headers: authHeaders(token, true),
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update code review policy: ${response.status}`);
  }

  const data = (await response.json()) as { data: PlanCodeReviewPolicyInput };
  return data.data;
};

export const patchPlanStartPolicy = async (
  token: string,
  projectId: string,
  planId: string,
  input: PlanTaskExecutionPolicyInput,
): Promise<PlanStartPolicyResult> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${planId}/start-policy`, {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update plan start policy: ${response.status}`);
  }

  const data = (await response.json()) as { data: PlanStartPolicyResult };
  return data.data;
};

export interface PlanConvertResult {
  id: string;
  contentVersion: string;
  sections: PlanSection[];
  tasks: PlanTask[];
  progress: PlanProgress | null;
}

/** BACKLOG 상태의 V1 플랜을 구조화 v2로 전환한다(베타 전용). 서버가 기존 본문을 파싱해 섹션/작업을 만든다. */
export const convertPlanToV2 = async (token: string, projectId: string, id: string): Promise<PlanConvertResult> => {
  // 본문 없는 POST. Content-Type: application/json을 붙이면 Fastify가 빈 JSON 본문으로 400을 내므로 붙이지 않는다.
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}/convert-to-v2`, {
    method: 'POST',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to convert plan: ${response.status}`);
  }

  const data = (await response.json()) as { data: PlanConvertResult };
  return data.data;
};

export const deletePlan = async (token: string, projectId: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete plan: ${response.status}`);
  }
};

export const assignPlan = async (token: string, projectId: string, id: string, assignedTo: string): Promise<Plan> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}/assign`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ assignedTo }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to assign plan: ${response.status}`);
  }

  const data = (await response.json()) as PlanResponse;
  return data.data;
};

/** 사람 담당자(책임 주체) 지정. 실행 주체 배정(assignPlan)과 별개 엔드포인트. */
export const assignPlanMember = async (
  token: string,
  projectId: string,
  id: string,
  assigneeMemberId: string,
): Promise<Plan> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}/assignee`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ assigneeMemberId }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to assign plan member: ${response.status}`);
  }

  const data = (await response.json()) as PlanResponse;
  return data.data;
};

// ============================================================
// Plan Dependency API
// ============================================================

export interface PlanDependency {
  id: string;
  dependentPlanId: string;
  blockingPlanId: string;
  createdAt: string;
}

export interface PlanDependencies {
  blocking: Plan[];
  dependents: Plan[];
}

type PlanDependenciesResponse = {
  data: PlanDependencies;
};

type PlanDependencyResponse = {
  data: PlanDependency;
};

export const fetchPlanDependencies = async (
  token: string,
  projectId: string,
  id: string,
): Promise<PlanDependencies> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}/dependencies`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch plan dependencies: ${response.status}`);
  }

  const data = (await response.json()) as PlanDependenciesResponse;
  return data.data;
};

export const addPlanDependency = async (
  token: string,
  projectId: string,
  id: string,
  blockingPlanId: string,
): Promise<PlanDependency> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${id}/dependencies`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ blockingPlanId }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to add plan dependency: ${response.status}`);
  }

  const data = (await response.json()) as PlanDependencyResponse;
  return data.data;
};

export const removePlanDependency = async (
  token: string,
  projectId: string,
  planId: string,
  depId: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/dependencies/${depId}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to remove plan dependency: ${response.status}`);
  }
};

export const removePlanDependencyByBlockingPlanId = async (
  token: string,
  projectId: string,
  planId: string,
  blockingPlanId: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/dependencies/blocking/${blockingPlanId}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to remove plan dependency by blocking plan: ${response.status}`);
  }
};

// --- Origin Issues ---

export interface PlanOriginIssue {
  id: string;
  planId: string;
  provider: string;
  externalId: string;
  externalUrl: string;
  externalTitle: string | null;
  externalStatus: string | null;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

type PlanOriginIssuesResponse = {
  data: PlanOriginIssue[];
};

type PlanOriginIssueResponse = {
  data: PlanOriginIssue;
};

export const fetchPlanOriginIssues = async (
  token: string,
  projectId: string,
  planId: string,
): Promise<PlanOriginIssue[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${planId}/origin-issues`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch origin issues: ${response.status}`);
  }

  const data = (await response.json()) as PlanOriginIssuesResponse;
  return data.data;
};

export type LinkOriginIssueInput = {
  provider: string;
  externalId: string;
  externalUrl: string;
  externalTitle?: string;
  metadata?: Record<string, unknown>;
};

export const linkPlanOriginIssue = async (
  token: string,
  projectId: string,
  planId: string,
  input: LinkOriginIssueInput,
): Promise<PlanOriginIssue> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${planId}/origin-issues`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to link origin issue: ${response.status}`);
  }

  const data = (await response.json()) as PlanOriginIssueResponse;
  return data.data;
};

export const unlinkPlanOriginIssue = async (
  token: string,
  projectId: string,
  planId: string,
  issueId: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/origin-issues/${issueId}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to unlink origin issue: ${response.status}`);
  }
};

// --- Linked Documents ---

export interface PlanDocumentLink {
  id: string;
  planId: string;
  documentId: string;
  title: string;
  visibility: 'PROJECT' | 'PRIVATE';
  archived: boolean;
  note: string | null;
  createdAt: string;
}

type PlanDocumentResponse = { data: PlanDocumentLink };

export const linkPlanDocument = async (
  token: string,
  projectId: string,
  planId: string,
  documentId: string,
): Promise<PlanDocumentLink> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${planId}/documents`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ documentId }),
  });
  if (!response.ok) {
    throw await parseApiError(response, `Failed to link document: ${response.status}`);
  }
  return ((await response.json()) as PlanDocumentResponse).data;
};

export const unlinkPlanDocument = async (
  token: string,
  projectId: string,
  planId: string,
  documentId: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/documents/${documentId}`,
    { method: 'DELETE', headers: authHeaders(token) },
  );
  if (!response.ok) {
    throw await parseApiError(response, `Failed to unlink document: ${response.status}`);
  }
};
